import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageTiredOfFeelingDepressed = makeArticle({
  slug: "tired-of-feeling-depressed-options-left",
  title: "Tired of Feeling Depressed? Options That Remain | Neuro Wellness",
  description:
    "If you're tired of feeling depressed all the time, learn about options that remain: TMS, SAINT®, SPRAVATO®, and other treatments for persistent depression.",
  canonical: "/learn/tired-of-feeling-depressed-options-left",
  h1: "I'm Tired of Feeling Depressed All the Time — What Options Are Left?",
  breadcrumbLabel: "Tired of Feeling Depressed",
  shortAnswer:
    "If you're tired of feeling depressed all the time and standard treatments haven't helped, options remain. TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), medication optimization, and psychotherapy are all evidence-based treatments for depression that hasn't responded to standard care. You don't have to keep feeling this way. A psychiatric evaluation can review your treatment history, identify what hasn't been tried, and recommend next steps. If you're having thoughts of self-harm, call or text 988 now.",
  keyTakeaways: [
    "Options remain even when standard treatments haven't helped.",
    "TMS, SAINT®, and SPRAVATO® work through different mechanisms.",
    "A psychiatric evaluation can identify what to try next.",
    "You don't have to keep feeling this way.",
    "If having thoughts of self-harm, call or text 988.",
  ],
  sections: [
    {
      heading: "You're Not Out of Options",
      paragraphs: [
        "If you're exhausted from feeling depressed and treatments haven't worked, it's understandable to feel discouraged. But you are not out of options. Depression that hasn't responded to standard antidepressants is common — it affects roughly one-third of people treated for major depressive disorder. Treatments like TMS, SAINT®, and SPRAVATO® work through different mechanisms than oral antidepressants and may help when medication hasn't.",
        "The first step is a comprehensive psychiatric evaluation. This reviews your treatment history, confirms whether past trials were adequate, screens for conditions that can mimic treatment resistance, and identifies what to try next.",
      ],
    },
    {
      heading: "Options That Remain",
      paragraphs: ["Options beyond standard antidepressants include:"],
      list: [
        "TMS — FDA-cleared magnetic brain stimulation, ~6 weeks, broadly covered by insurance",
        "SAINT® — 5-day accelerated TMS with fMRI targeting, FDA-cleared",
        "SPRAVATO® — FDA-approved nasal spray for treatment-resistant depression",
        "Medication optimization — adjusting dose, switching, or augmenting",
        "Psychotherapy — CBT, behavioral activation, interpersonal therapy",
        "ECT — for severe or urgent cases, hospital-based",
      ],
    },
    {
      heading: "What to Do Next",
      paragraphs: [
        "If you're tired of feeling depressed, reach out for a psychiatric evaluation. Bring your medication history, including what you've tried, at what dose, for how long, and why you stopped. This helps your psychiatrist determine whether past trials were adequate and what to try next. You don't have to figure this out alone.",
      ],
    },
    {
      heading: "If You're Having Thoughts of Self-Harm",
      paragraphs: [
        "If you're having thoughts of harming yourself or feeling that you'd be better off dead, please call or text 988 (Suicide & Crisis Lifeline) now, or go to your nearest emergency room. You can also chat at 988lifeline.org. These feelings are a medical emergency, and help is available right now.",
      ],
    },
  ],
  faqs: [
    {
      question:
        "I'm tired of feeling depressed all the time — what options are left?",
      answer:
        "Options include TMS, SAINT® accelerated TMS, SPRAVATO®, medication optimization, and psychotherapy. These work through different mechanisms than standard antidepressants. A psychiatric evaluation can review your history and recommend next steps. If having thoughts of self-harm, call or text 988.",
    },
    {
      question: "What if nothing has worked for my depression?",
      answer:
        "If standard treatments haven't helped, options remain. TMS, SAINT®, and SPRAVATO® are FDA-cleared or approved for treatment-resistant depression. A psychiatric evaluation can identify what hasn't been tried and what may help. You're not out of options.",
    },
    {
      question: "Is there hope after multiple depression treatments fail?",
      answer:
        "Yes. Depression that hasn't responded to multiple treatments is common and treatable. Advanced treatments like TMS, SAINT®, and SPRAVATO® work through different mechanisms than antidepressants. A psychiatric evaluation can identify next steps.",
    },
    {
      question: "What should I do if I'm exhausted from depression?",
      answer:
        "Reach out for a psychiatric evaluation. Bring your medication history — what you've tried, at what dose, for how long, and why you stopped. This helps your psychiatrist determine what to try next. If having thoughts of self-harm, call or text 988 now.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
